import type { ReactNode } from "react";
import {
  Alert,
  Center,
  Group,
  Loader,
  Pagination,
  Stack,
  Table,
  Text,
} from "@mantine/core";
import { IconAlertTriangle, IconInbox } from "@tabler/icons-react";

export interface Column<T> {
  key: string;
  header: ReactNode;
  render: (row: T) => ReactNode;
  width?: number | string;
  align?: "left" | "center" | "right";
}

interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  loading?: boolean;
  error?: string | null;
  emptyLabel?: string;
  /** Optional app illustration shown above the empty label. */
  emptyImage?: string;
  onRowClick?: (row: T) => void;
  // Server pagination (optional).
  page?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  total?: number;
}

// Generic, presentation-only table. Each module supplies columns + a data hook;
// this owns loading/empty/error UI and pagination so screens stay thin.
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  loading,
  error,
  emptyLabel = "Nothing here yet",
  emptyImage,
  onRowClick,
  page,
  totalPages,
  onPageChange,
  total,
}: DataTableProps<T>) {
  const showPagination =
    !!onPageChange && !!totalPages && totalPages > 1 && !!page;

  return (
    <Stack gap="sm">
      <Table.ScrollContainer minWidth={640}>
        <Table
          striped
          highlightOnHover={!!onRowClick}
          verticalSpacing="sm"
          horizontalSpacing="md"
        >
          <Table.Thead>
            <Table.Tr>
              {columns.map((c) => (
                <Table.Th
                  key={c.key}
                  style={{ width: c.width, textAlign: c.align }}
                >
                  {c.header}
                </Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {loading && (
              <Table.Tr>
                <Table.Td colSpan={columns.length}>
                  <Center py="xl">
                    <Loader />
                  </Center>
                </Table.Td>
              </Table.Tr>
            )}

            {!loading && error && (
              <Table.Tr>
                <Table.Td colSpan={columns.length}>
                  <Alert
                    color="red"
                    icon={<IconAlertTriangle size={16} />}
                    variant="light"
                    my="sm"
                  >
                    {error}
                  </Alert>
                </Table.Td>
              </Table.Tr>
            )}

            {!loading && !error && rows.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={columns.length}>
                  <Center py="xl">
                    <Stack align="center" gap={4}>
                      {emptyImage ? (
                        <img src={emptyImage} alt="" aria-hidden style={{ maxWidth: 180, maxHeight: 110, opacity: 0.9 }} />
                      ) : (
                        <IconInbox size={28} opacity={0.4} />
                      )}
                      <Text c="dimmed" size="sm">
                        {emptyLabel}
                      </Text>
                    </Stack>
                  </Center>
                </Table.Td>
              </Table.Tr>
            )}

            {!loading &&
              !error &&
              rows.map((row) => (
                <Table.Tr
                  key={rowKey(row)}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  style={{ cursor: onRowClick ? "pointer" : undefined }}
                >
                  {columns.map((c) => (
                    <Table.Td key={c.key} style={{ textAlign: c.align }}>
                      {c.render(row)}
                    </Table.Td>
                  ))}
                </Table.Tr>
              ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      {showPagination && (
        <Group justify="space-between">
          <Text size="sm" c="dimmed">
            {total !== undefined ? `${total} total` : ""}
          </Text>
          <Pagination
            value={page}
            total={totalPages}
            onChange={onPageChange}
            size="sm"
          />
        </Group>
      )}
    </Stack>
  );
}
